import { motion } from "framer-motion";
import { Mail, MessageCircle, Instagram, Facebook } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { WHATSAPP_URL, CONTACT_EMAIL } from "@/data/translations";

/**
 * Footer — split-curtain entrance.
 * Three columns: links, social, legal. Giant wordmark watermark at bottom (10% opacity).
 * Final line in DM Mono, saffron: "Fait avec soin — de Casablanca à Montréal."
 */
const Footer = () => {
  const { t } = useLang();

  return (
    <footer
      data-testid="footer"
      className="relative bg-[#0D1117] border-t border-[#F5F2EE]/8 overflow-hidden"
    >
      <div className="noise-soft" />

      <motion.div
        initial={{ y: 30, opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
        className="relative max-w-[1320px] mx-auto px-6 lg:px-10 pt-20 pb-12"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Links */}
          <div className="md:col-span-4">
            <div className="font-mono-accent text-[10px] text-[#E8A045] mb-6">
              {t.footer.links}
            </div>
            <ul className="space-y-4 text-sm text-[#F5F2EE]/70 font-body">
              <li><a href="#how" data-testid="footer-link-process" className="hover:text-[#E8A045] transition-colors">{t.nav.processus}</a></li>
              <li><a href="#services" data-testid="footer-link-services" className="hover:text-[#E8A045] transition-colors">{t.nav.services}</a></li>
              <li><a href="#testimonials" data-testid="footer-link-testimonials" className="hover:text-[#E8A045] transition-colors">{t.nav.testimonials}</a></li>
              <li><a href="/about" data-testid="footer-link-about" className="hover:text-[#E8A045] transition-colors">{t.footer.about}</a></li>
              <li><a href="/faq" data-testid="footer-link-faq" className="hover:text-[#E8A045] transition-colors">{t.footer.faq}</a></li>
              <li><a href="#contact" data-testid="footer-link-contact" className="hover:text-[#E8A045] transition-colors">{t.nav.contact}</a></li>
            </ul>
          </div>

          {/* Social */}
          <div className="md:col-span-4">
            <div className="font-mono-accent text-[10px] text-[#E8A045] mb-6">
              Social
            </div>
            <div className="flex items-center gap-4">
              <a
                href="https://www.instagram.com/marocanada.immigration"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                className="w-10 h-10 border border-[#F5F2EE]/12 flex items-center justify-center text-[#F5F2EE]/65 hover:text-[#E8A045] hover:border-[#E8A045]/40 transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-4 h-4" strokeWidth={1.3} />
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61590726581579"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-facebook"
                className="w-10 h-10 border border-[#F5F2EE]/12 flex items-center justify-center text-[#F5F2EE]/65 hover:text-[#E8A045] hover:border-[#E8A045]/40 transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-4 h-4" strokeWidth={1.3} />
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-whatsapp"
                className="w-10 h-10 border border-[#F5F2EE]/12 flex items-center justify-center text-[#F5F2EE]/65 hover:text-[#E8A045] hover:border-[#E8A045]/40 transition-colors"
                aria-label="WhatsApp"
              >
                <MessageCircle className="w-4 h-4" strokeWidth={1.3} />
              </a>
            </div>

            <div className="mt-8 space-y-3 text-sm text-[#F5F2EE]/65">
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                data-testid="footer-email"
                className="flex items-center gap-3 hover:text-[#E8A045] transition-colors"
              >
                <Mail className="w-4 h-4 text-[#E8A045]" strokeWidth={1.3} />
                {CONTACT_EMAIL}
              </a>
            </div>
          </div>

          {/* Legal */}
          <div className="md:col-span-4">
            <div className="font-mono-accent text-[10px] text-[#E8A045] mb-6">
              Legal
            </div>
            <ul className="space-y-3 text-sm text-[#F5F2EE]/55 font-body">
              <li><a href="#" className="hover:text-[#E8A045] transition-colors">{t.footer.privacy}</a></li>
              <li><a href="#" className="hover:text-[#E8A045] transition-colors">{t.footer.terms}</a></li>
              <li className="pt-3 text-xs text-[#F5F2EE]/40 leading-relaxed">
                Casablanca, Maroc · Montréal, Canada
              </li>
            </ul>
          </div>
        </div>

        {/* Language selector mini */}
        <div className="mt-12 pt-8 border-t border-[#F5F2EE]/8 flex flex-wrap items-center justify-between gap-4">
          <span data-testid="footer-legal" className="font-mono-accent text-[10px] text-[#F5F2EE]/45">
            {t.footer.legal}
          </span>
          <span className="font-mono-accent text-[10px] text-[#E8A045]">
            Fait avec soin — de Casablanca à Montréal.
          </span>
        </div>
      </motion.div>

      {/* GIANT WORDMARK WATERMARK at bottom */}
      <div
        dir="ltr"
        className="relative pointer-events-none select-none overflow-hidden"
        aria-hidden
      >
        <div className="font-display text-[20vw] leading-[0.85] tracking-tighter text-[#F5F2EE] whitespace-nowrap text-center px-4 pb-2 mb-[-3vw]"
             style={{ opacity: 0.07 }}>
          <span style={{ color: "#9B7548" }}>Maro</span>
          <span className="italic" style={{ color: "#F5F2EE" }}>canada</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
